import { useAppMutation } from '@/shared/hooks/useAppMutation'
import { updateGiftCardProfile } from '../api'
import type { UpdateGiftCardProfileRequest, UpdateGiftCardProfileResponse } from '../types'
import { balancesKeys } from '@/features/balances/hooks/queryKeys'
import { getBalances } from '@/features/balances/api'
import { ApiError } from '@/shared/lib/api'
import { useAuthStore } from '@/shared/stores/auth'
import { useCountryStore } from '@/shared/stores/country'
import { detectCountry } from '@/shared/constants/countries'

export interface UseUpdateProfileReturn {
  mutate: (data: UpdateGiftCardProfileRequest) => Promise<UpdateGiftCardProfileResponse | null>
  isPending: boolean
  data: UpdateGiftCardProfileResponse | null
  error: Error | null
}

export function syncUpdatedProfile(user: UpdateGiftCardProfileResponse) {
  useAuthStore.getState().setUser(user)

  if (user.prefCountry) {
    localStorage.setItem('preferredCountry', user.prefCountry)
    useCountryStore.getState().setCountry(user.prefCountry)
  } else if (user.prefCountry === null) {
    localStorage.removeItem('preferredCountry')
    useCountryStore.getState().setCountry(detectCountry(), false)
  }
}

export function useUpdateProfile(): UseUpdateProfileReturn {
  return useAppMutation({
    mutationFn: (payload: UpdateGiftCardProfileRequest) => updateGiftCardProfile(payload),
    onSuccess: (updatedUser, _payload, queryClient) => {
      syncUpdatedProfile(updatedUser)
      queryClient.setQueryData(balancesKeys.me(), updatedUser)
      void queryClient.invalidateQueries({ queryKey: balancesKeys.me() })
    },
    onError: async (error, _payload, queryClient) => {
      // A server or transport failure can occur after the update committed.
      // Validation/auth failures are definitive; never replay a failed POST.
      if (error instanceof ApiError && error.status < 500 && error.status !== 408) return

      const userId = useAuthStore.getState().user?.id
      if (userId === undefined || !useAuthStore.getState().isAuthenticated) return

      const refreshedUser = await getBalances()
      const currentAuth = useAuthStore.getState()
      // Ignore a read that completed after logout or an account switch.
      if (
        !currentAuth.isAuthenticated ||
        currentAuth.user?.id !== userId ||
        refreshedUser.id !== userId
      )
        return

      syncUpdatedProfile(refreshedUser)
      queryClient.setQueryData(balancesKeys.me(), refreshedUser)
    },
    mapResult: (data) => data,
  })
}
